/* eslint-disable prefer-destructuring */
import { useDisclosure } from '@chakra-ui/react';
import { IQuoteRespSchema, QuoteStatusEnum } from '@monetize/types/app';
import React, { FunctionComponent as FC } from 'react';
import { useGetEsignRecipients } from '../../../api/cpqService';
import {
  QUOTE_ACCEPTED_STATUSES,
  QUOTE_SENT_STATUSES,
} from '../../../constants/quotes';
import { MBox } from '../../Monetize';
import { QuoteProgressStep } from './QuoteProgressStep';
import { QuoteStepEsignRecipients } from './QuoteStepEsignRecipients';

interface QuoteStepSignProps {
  quote: IQuoteRespSchema;
  pauseAtAccepted: boolean;
  stepMessage: {
    stepDate: string;
    stepTitle: string;
    isError: boolean;
    showLightBackground: boolean;
  } | null;
}

export const QuoteStepSign: FC<QuoteStepSignProps> = React.forwardRef<
  any,
  QuoteStepSignProps
>(({ quote, pauseAtAccepted, stepMessage }: QuoteStepSignProps, ref) => {
  const popoverState = useDisclosure();
  const { isRefetching: isRefetchingEsignRecipients } = useGetEsignRecipients(
    quote.id,
    { enabled: popoverState.open && !!quote.esignEnvelopeId },
  );

  let stepTitle = quote.requiresEsign
    ? 'Sign Deal with Customer'
    : 'Confirm Customer Approval';
  let stepDate: string | null = quote.acceptedAt ?? null;

  const isCompleted = pauseAtAccepted
    ? QUOTE_ACCEPTED_STATUSES.has(quote.status)
    : QuoteStatusEnum.PROCESSED === quote.status && !!quote.acceptedAt;

  const showInprogressStepNumber = false;
  const isCurrent = pauseAtAccepted
    ? QuoteStatusEnum.SENT === quote.status
    : QUOTE_SENT_STATUSES.has(quote.status);
  const isDisabled = !isCurrent && !QUOTE_SENT_STATUSES.has(quote.status);

  if (isCompleted) {
    stepDate = quote.acceptedAt;
    if (quote.autoRenewal) {
      stepTitle = 'Automatically Renewed';
    } else if (!quote.requiresEsign && pauseAtAccepted) {
      stepTitle = 'Marked as Accepted';
    } else if (!quote.requiresEsign && !pauseAtAccepted) {
      stepTitle = 'Automatically Processed';
    } else {
      stepTitle = 'Signed by Customer';
    }
  }
  if (stepMessage) {
    stepTitle = stepMessage.stepTitle;
    stepDate = stepMessage.stepDate;
  }

  return (
    <QuoteProgressStep
      key="quote-progress-sign"
      stepTitle={stepTitle}
      stepDate={stepDate}
      stepNumber={4}
      isCompleted={isCompleted}
      showInprogressStepNumber={showInprogressStepNumber}
      isDisabled={isDisabled}
      isCurrent={isCurrent}
      containerProps={{ ml: -8 }}
      isError={stepMessage?.isError}
      showLightBackground={stepMessage?.showLightBackground}
      popoverState={popoverState}
      popoverProps={{ closeOnInteractOutside: !isRefetchingEsignRecipients }}
      showStepPopover={!!quote.esignEnvelopeId}
      popoverContent={
        quote.esignEnvelopeId
          ? () => (
              <MBox p="2">
                <MBox
                  maxH="350px"
                  className="custom-scroll-bar-v1"
                  overflowY="auto"
                  pr="2"
                >
                  <QuoteStepEsignRecipients
                    quoteId={quote.id}
                    contacts={quote.contacts}
                    quoteStatus={quote.status}
                  />
                </MBox>
              </MBox>
            )
          : undefined
      }
    />
  );
});
